import React from 'react';
import { Quote, Award, CheckCircle, Mail, Phone, ArrowUpRight, GraduationCap } from 'lucide-react';
import { INSTITUTION_INFO } from '../data/portalData';
import { SocialIconLinks } from './SocialLinks';

export const DirectorMessage = ({ setActiveView }) => {
  const { director } = INSTITUTION_INFO;

  return (
    <section id="director" className="py-20 bg-white border-b-2 border-slate-200 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left: Director's Executive Portrait & Credentials Card */}
          <div className="lg:col-span-5">
            <div className="relative mx-auto max-w-sm lg:max-w-none">
              
              {/* Decorative Background Accents in Navy and Gold */}
              <div className="absolute -top-4 -left-4 w-full h-full rounded-3xl bg-[#0B2644]/10 -z-10 transform -rotate-1" />
              <div className="absolute -bottom-4 -right-4 w-full h-full rounded-3xl bg-[#7A470E]/15 -z-10 transform rotate-1" />

              <div className="translucent-card-light rounded-3xl overflow-hidden shadow-2xl border-2 border-[#7A470E]/40">
                {/* Director Avatar / Professional Executive Illustration */}
                <div className="relative bg-gradient-to-tr from-[#030F1C] via-[#06182B] to-[#0B2644] pt-8 px-6 text-center border-b-2 border-[#7A470E]">
                  <div className="inline-block relative">
                    <img 
                      src="/director-uwah.png" 
                      alt={director.name}
                      className="w-36 h-36 rounded-full mx-auto object-cover object-top border-4 border-[#7A470E] shadow-xl"
                    />
                    <div className="absolute bottom-1 right-2 bg-[#7A470E] text-white rounded-full p-1.5 shadow" title="Verified Faculty Fellow">
                      <Award className="w-4 h-4" />
                    </div>
                  </div>

                  <div className="mt-4 pb-6 text-white">
                    <h3 className="text-2xl font-black font-sc tracking-wide text-white">
                      {director.name}
                    </h3>
                    <p className="text-[#B87A28] text-xs font-bold tracking-wider uppercase mt-1 font-sc">
                      {director.qualifications}
                    </p>
                    <p className="text-slate-300 text-xs mt-1">
                      {director.title}
                    </p>
                  </div>
                </div>

                {/* Director Professional Affiliations */}
                <div className="p-6 space-y-4 bg-slate-50 text-xs">
                  <div className="flex items-start space-x-3 text-slate-700">
                    <CheckCircle className="w-4 h-4 text-[#7A470E] shrink-0 mt-0.5" />
                    <span>{director.fellowship}</span>
                  </div>
                  <div className="flex items-start space-x-3 text-slate-700">
                    <CheckCircle className="w-4 h-4 text-[#7A470E] shrink-0 mt-0.5" />
                    <span>Senior Academic in Accounting & Corporate Financial Forensics</span>
                  </div>
                  <div className="flex items-start space-x-3 text-slate-700">
                    <CheckCircle className="w-4 h-4 text-[#7A470E] shrink-0 mt-0.5" />
                    <span>Member, University Senate Committee on Executive Postgraduates</span>
                  </div>

                  {/* Direct Contact Links */}
                  <div className="pt-3 border-t border-slate-200 flex flex-col space-y-2">
                    <a 
                      href={`mailto:${director.email}`}
                      className="flex items-center text-slate-700 hover:text-[#0B2644] transition-colors"
                    >
                      <Mail className="w-3.5 h-3.5 mr-2 text-[#0B2644]" />
                      <span>{director.email}</span>
                    </a>
                    <a 
                      href={`tel:${director.phone}`}
                      className="flex items-center text-slate-700 hover:text-[#7A470E] transition-colors"
                    >
                      <Phone className="w-3.5 h-3.5 mr-2 text-[#7A470E]" />
                      <span>{director.phone} (Admissions Desk)</span>
                    </a>
                    <div className="pt-2 border-t border-slate-200/80 flex items-center justify-between">
                      <span className="text-[11px] text-slate-500 font-sc font-medium">Follow Updates:</span>
                      <SocialIconLinks theme="light" iconClassName="w-3.5 h-3.5" />
                    </div>
                  </div>
                </div>

              </div>

            </div>
          </div>

          {/* Right: Visionary Statement & School Philosophy */}
          <div className="lg:col-span-7 space-y-6">
            
            <div className="inline-flex items-center space-x-2 text-xs font-black uppercase tracking-widest text-[#0B2644] bg-slate-100 px-3 py-1 rounded-lg border border-slate-300 font-sc">
              <GraduationCap className="w-3.5 h-3.5 text-[#7A470E]" />
              <span>Director's Welcome Statement</span>
            </div>

            <h2 className="text-2xl sm:text-4xl font-black text-[#0B2644] font-sc leading-tight">
              "Equipping Industry Leaders to Navigate Complexity and Spearhead Sustainable Growth"
            </h2>

            <div className="relative p-6 rounded-2xl translucent-card-light border-l-4 border-[#7A470E] italic text-slate-800 text-base sm:text-lg leading-relaxed font-normal shadow-sm">
              <p>
                "On behalf of the management, faculty, and staff of the Akwa Ibom State University (AKSU) Business School, 
                I am pleased to welcome you to a community dedicated to shaping principled, knowledgeable, and enterprising 
                leaders for Nigeria's public and private sectors."
              </p>
            </div>

            <p className="text-slate-600 text-sm leading-relaxed">
              Whether you are an accountant seeking professional advancement, a civil servant preparing for greater 
              responsibility, a business executive sharpening your strategic edge, or a recent graduate beginning a 
              postgraduate journey, AKSU Business School offers a pathway built on academic rigour, practical relevance, 
              and professional recognition.
            </p>

            <p className="text-slate-600 text-sm leading-relaxed">
              We invite you to explore our prospectus and to take the next step toward a Certificate, Diploma, 
              Postgraduate Diploma, Master's, or Doctorate qualification with us. Operating from the University's 
              Obio Akpa Campus with a dedicated Liaison Office in Shelter Afrique, Uyo, our hybrid executive delivery 
              combines full university academic depth with executive flexibility.
            </p>

            {/* Signature & Direct Console Access */}
            <div className="pt-4 flex flex-wrap items-center justify-between gap-4 border-t border-slate-200">
              <div>
                <div className="font-sans font-black text-xl text-[#0B2644] tracking-wide font-sc">
                  Dr Uwem Etim Uwah, <span className="font-sans font-bold text-base text-[#7A470E]">PhD, FCA, ACTI</span>
                </div>
                <div className="text-xs text-slate-500 font-sc font-semibold mt-0.5">
                  Director, AKSU Business School · Associate Professor of Accounting
                </div>
              </div>

              <button
                onClick={() => setActiveView('lms-login-director')}
                className="inline-flex items-center space-x-1.5 text-xs font-bold text-white bg-[#0B2644] hover:bg-[#06182B] px-4 py-2.5 rounded-xl shadow-xs transition-colors cursor-pointer font-sc border border-[#7A470E]/40"
              >
                <span>Enter Director's Console</span>
                <ArrowUpRight className="w-4 h-4 text-[#B87A28]" />
              </button>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
};
